import * as React from "react";
import { QESButton, QESMultiSelect } from "@app/shared/qes-standards";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { orderBy } from "lodash";
import { ClientRoleModel } from "@app/client/clientRoleModel";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { Roles, RoleType } from "@app/user";
import { GlobalConstants } from "@app/shared/constants";

interface ClientDefaultUserRolesProps {
  disabled?: boolean;
}

export const ClientDefaultUserRoles: React.FC<React.PropsWithChildren<ClientDefaultUserRolesProps>> = (props: ClientDefaultUserRolesProps) => {

  const clientContext = React.useContext(ClientContext);

  /* defaultUserRoles */
  const selectedDefaultUserOptions = React.useMemo(() => (clientContext.defaultUserRoles !== undefined)
    ? clientContext.defaultUserRoles.map((r) => r.name) : []
    , [clientContext.defaultUserRoles]);

  const defaultUserRoleOptions = React.useMemo(() => {
    const innerRoleOptions: { [key: string]: MultiselectItem } = {};
    clientContext.authorizedRoles.filter(r =>
      // Always show public roles
      (r.roleType === RoleType.Public
        // For "internal" clients, also show internal use only roles
        || (clientContext.isQuestInternal && r.roleType === RoleType.InternalUseOnly)
        // for Quest client, show Quest client only roles
        || (clientContext.clientId == GlobalConstants.QuestClientId && r.roleType === RoleType.QuestClientOnly)
      )
      // ... but never show Resource Admin, Client Admin or Quest Viewer as potential default roles for a new user - these must be explicitly assigned
      && (r.name !== Roles.QUEST_ADMIN && r.name !== Roles.RESOURCE_ADMIN && r.name !== Roles.CLIENT_ADMIN && r.name !== Roles.QUEST_VIEWER))
      .forEach((r) => Object.assign(innerRoleOptions,
        {
          [r.name]: {
            currentState: selectedDefaultUserOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name,
          }
        })
      );
    return innerRoleOptions;
  }, [clientContext.authorizedRoles, selectedDefaultUserOptions, clientContext.isQuestInternal]);

  const updateDefaultUserRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = clientContext.authorizedRoles.find((r) => r.name === role);
      return matchedRoleModel;
    }).filter((r) => !!r);
    clientContext.setDefaultUserRoles(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  /* resourceAdminGuestUserRoles */

  const selectedResourceAdminGuestUserOptions = React.useMemo(() => (clientContext.resourceAdminGuestUserRoles !== undefined)
    ? clientContext.resourceAdminGuestUserRoles.map((r) => r.name) : []
    , [clientContext.resourceAdminGuestUserRoles]);

  const resourceAdminRoleOptions = React.useMemo(() => {
    const innerRoleOptions: { [key: string]: MultiselectItem } = {};
    // available default roles for Resource Admin should not include Quest Admin or Quest Viewer    
   clientContext.authorizedRoles.filter(r => r.name !== Roles.QUEST_ADMIN && r.name !== Roles.QUEST_VIEWER).forEach((r) => Object.assign(innerRoleOptions,
      {
        [r.name]: {
          currentState: selectedResourceAdminGuestUserOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: r.name,
        }
      })
    );
    return innerRoleOptions;
  }, [clientContext.authorizedRoles, selectedResourceAdminGuestUserOptions]);

  const updateResourceAdminGuestUserRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = clientContext.authorizedRoles.find((r) => r.name === role);
      return matchedRoleModel;
    }).filter((r) => !!r);
    clientContext.setResourceAdminGuestUserRoles(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  /* questViewerGuestUserRoles */
  const selectedQuestViewerGuestUserOptions = React.useMemo(() => (clientContext.questViewerGuestUserRoles !== undefined)
    ? clientContext.questViewerGuestUserRoles.map((r) => r.name) : []
    , [clientContext.questViewerGuestUserRoles]);

  const questViewerGuestUserRoleOptions = React.useMemo(() => {
    const innerRoleOptions: { [key: string]: MultiselectItem } = {};
    // Available roles for Quest viewer should not include Quest Admin, Resource Admin or Client Admin
    clientContext.authorizedRoles.filter(r => r.name !== Roles.QUEST_ADMIN && r.name !== Roles.RESOURCE_ADMIN && r.name !== Roles.CLIENT_ADMIN).forEach((r) => Object.assign(innerRoleOptions,
      {
        [r.name]: {
          currentState: selectedQuestViewerGuestUserOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: r.name,
        }
      })
    );
    return innerRoleOptions;
  }, [clientContext.authorizedRoles, selectedQuestViewerGuestUserOptions]);

  const updateQuestViewerGuestUserRoles = (response: MultiselectOnChangeResponse) => {
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = clientContext.authorizedRoles.find((r) => r.name === role);
      return matchedRoleModel;
    }).filter((r) => !!r);
    clientContext.setQuestViewerGuestUserRoles(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  const onClearAll = (setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    setterFunc(Array<ClientRoleModel>());
  }

  const onSelectAll = (source: ClientRoleModel[], setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    const newRoles = source.map((r) => r);
    setterFunc(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  const onTranscribe = (allOptions: ClientRoleModel[],
    source: ClientRoleModel[],
    setterFunc: React.Dispatch<React.SetStateAction<ClientRoleModel[]>>) => {
    const newRoles = source.map((sourceItem) => {
      const matchedRoleModel = allOptions.find((r) => r.name === sourceItem.name);
      return matchedRoleModel;
    }).filter((r) => !!r);
    setterFunc(orderBy(newRoles, (nr) => nr.name, "asc"));
  }

  return (
    <div className="col-12">
      <QESMultiSelect
        containerClassName=""
        disabled={props.disabled}
        errorText="At least one role must be selected."
        label="Default User Roles"
        onChange={updateDefaultUserRoles}
        options={defaultUserRoleOptions}
        prefix="defaultUserRoles"
        required={false}
        testId="default-client__defaultuserroles"
        isValid={clientContext.validationObject["defaultUserRoles"]}
      />
      <QESButton
        qesTestId="default-client__defautuserroles-SelectAll"
        qesType="secondary"
        onClick={() => onSelectAll(clientContext.authorizedRoles, clientContext.setDefaultUserRoles)}>
        Select All</QESButton>
      <QESButton
        qesTestId="default-client__defautuserroles-ClearAll"
        qesType="secondary"
        onClick={() => onClearAll(clientContext.setDefaultUserRoles)}>
        Clear All</QESButton>
      <QESMultiSelect
        containerClassName=""
        disabled={props.disabled}
        errorText="At least one role must be selected."
        label="Resource Admin Guest User Roles"
        onChange={updateResourceAdminGuestUserRoles}
        options={resourceAdminRoleOptions}
        prefix="resourceAdminRoles"
        required={false}
        testId="default-client__resourceadminguestuserroles"
        isValid={clientContext.validationObject["resourceAdminGuestUserRoles"]}
      />
      <QESButton
        qesTestId="default-client__resourceadminroles-SelectAll"
        qesType="secondary"
        onClick={() => onSelectAll(clientContext.authorizedRoles, clientContext.setResourceAdminGuestUserRoles)}>
        Select All</QESButton>
      <QESButton
        qesTestId="default-client__resourceadminroles-ClearAll"
        qesType="secondary"
        onClick={() => onClearAll(clientContext.setResourceAdminGuestUserRoles)}>
        Clear All</QESButton>
      <QESButton
        qesTestId="default-client__resourceadminroles-copy_from_quest_viewer"
        qesType="secondary"
        onClick={() => onTranscribe(clientContext.authorizedRoles, clientContext.questViewerGuestUserRoles, clientContext.setResourceAdminGuestUserRoles)}>
        Copy from Quest Viewer</QESButton>
      <QESButton
        qesTestId="default-client__resourceadminroles-copy_from_default_user"
        qesType="secondary"
        onClick={() => onTranscribe(clientContext.authorizedRoles, clientContext.defaultUserRoles, clientContext.setResourceAdminGuestUserRoles)}>
        Copy from Default User</QESButton>
      <QESMultiSelect
        containerClassName=""
        disabled={props.disabled}
        errorText="At least one role must be selected."
        label="Quest Viewer Guest Login Roles"
        onChange={updateQuestViewerGuestUserRoles}
        options={questViewerGuestUserRoleOptions}
        prefix="questViewerRoles"
        required={false}
        testId="default-client__questviewererroles"
        isValid={clientContext.validationObject["questViewerGuestUserRoles"]}
      />
      <QESButton
        qesTestId="default-client__questviewerroles-SelectAll"
        qesType="secondary"
        onClick={() => onSelectAll(clientContext.authorizedRoles, clientContext.setQuestViewerGuestUserRoles)}>
        Select All</QESButton>
      <QESButton
        qesTestId="default-client__questviewerroles-ClearAll"
        qesType="secondary"
        onClick={() => onClearAll(clientContext.setQuestViewerGuestUserRoles)}>
        Clear All</QESButton>
      <QESButton
        qesTestId="default-client__questviewerroles-copy_from_resource_admin"
        qesType="secondary"
        onClick={() => onTranscribe(clientContext.authorizedRoles, clientContext.resourceAdminGuestUserRoles, clientContext.setQuestViewerGuestUserRoles)}>
        Copy from Resource Admin</QESButton>
      <QESButton
        qesTestId="default-client__questviewerroles-copy_from_resource_admin"
        qesType="secondary"
        onClick={() => onTranscribe(clientContext.authorizedRoles, clientContext.defaultUserRoles, clientContext.setQuestViewerGuestUserRoles)}>
        Copy from Default User</QESButton>
    </div>
  );
}
